import { useState, useCallback } from 'react';
import { Vendor, VendorStatsData, VendorListResponse, ApiResponse, VendorCreateRequest } from '../types'; // 경로 주의

export const useVendorData = () => {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [stats, setStats] = useState<VendorStatsData>({
    totalCount: 0, activeCount: 0, avgCommission: 0, basicCategoryCount: 0
  });
  const [isLoading, setIsLoading] = useState(false);

  const normalizeBoolean = (value: unknown) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
      const lowered = value.trim().toLowerCase();
      return lowered === 'y' || lowered === 'yes' || lowered === 'true' || lowered === '1';
    }
    return false;
  };

  const resolveMyVendorIdx = () => {
    try {
      const raw = localStorage.getItem('userInfo');
      if (!raw) return null;
      const user = JSON.parse(raw);
      const parsed = Number(user?.vendorIdx);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    } catch {
      return null;
    }
  };

  const isPartnershipVendor = (vendor: any, myVendorIdx: number | null) => {
    const contextType = String(vendor?.contextType ?? '').trim().toUpperCase();
    const relationshipType = String(vendor?.relationshipType ?? '').trim().toUpperCase();

    if (contextType === 'OWNER') return false;
    if (contextType === 'PARTNER') return true;
    if (relationshipType.includes('PARTNER')) return true;
    if (
      normalizeBoolean(vendor?.isPartnerVendor) ||
      normalizeBoolean(vendor?.isPartnershipVendor) ||
      normalizeBoolean(vendor?.isGuestContext)
    ) {
      return true;
    }
    if (normalizeBoolean(vendor?.isOwnerVendor)) return false;

    const vendorIdx = Number(vendor?.vendorIdx);
    if (myVendorIdx !== null && vendorIdx === myVendorIdx) return false;

    const ownerVendorIdx = Number(vendor?.ownerVendorIdx);
    if (myVendorIdx !== null && Number.isFinite(ownerVendorIdx) && ownerVendorIdx > 0) {
      if (ownerVendorIdx !== myVendorIdx) return true;
    }

    const hostVendorIdx = Number(vendor?.hostVendorIdx);
    if (myVendorIdx !== null && Number.isFinite(hostVendorIdx) && hostVendorIdx > 0) {
      if (hostVendorIdx !== myVendorIdx) return true;
    }

    return false;
  };

  const mapStatusToUi = (status: string) => {
    switch (status) {
      case 'ACTIVE': return '활성';
      case 'INACTIVE': return '중단';

      case 'PENDING': return '대기';
      case 'TEMPORARY': return '임시';
      default: return '활성';
    }
  };

  const mapUiStatusToApi = (uiStatus: string) => {
    switch (uiStatus) {
      case '활성': return 'ACTIVE';
      case '대기': return 'PENDING';
      case '임시': return 'TEMPORARY';
      case '중단': return 'INACTIVE';
      default: return '';
    }
  };

  const fetchVendors = useCallback(async (statusFilter: string, searchTerm: string, categoryFilterIdx: number | null) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const params = new URLSearchParams();
      params.append('page', '1');
      params.append('size', '100');
      
      if (statusFilter !== '전체') {
        params.append('status', mapUiStatusToApi(statusFilter));
      }

      if (typeof categoryFilterIdx === 'number') {
        params.append('categoryIdx', String(categoryFilterIdx));
      }

      const response = await fetch(`https://api.wedmin.biz/vendor?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('업체 목록 조회 실패');
      
      const json: ApiResponse<VendorListResponse> = await response.json();

      if (json.status && json.data) {
        const myVendorIdx = resolveMyVendorIdx();
        const mappedVendors: Vendor[] = json.data.vendors.map(v => {
          const partnership = isPartnershipVendor(v, myVendorIdx);

          return {
            id: v.vendorIdx.toString(),
            name: v.name,
            categoryIdx: v.category?.vendorCategoryIdx ?? null,
            category: v.category?.name || '미지정',
            subCategory: v.subCategoryName || '',
            contact: v.contact,
            manager: v.managerName,
            email: v.email,
            address: v.address,
            businessNumber: v.businessNumber,
            commissionRate: v.commissionRate,
            status: mapStatusToUi(v.status),
            vendorType: v.vendorType || 'local',
            canInvite: partnership
              ? false
              : (typeof v.canInvite === 'boolean' ? v.canInvite : true),
            ownershipType: partnership ? 'PARTNER' : 'OWNED',
            isPartnership: partnership,
            createdDt: new Date(v.createdDt || v.createDt || Date.now()).toLocaleDateString(),
            updatedDt: new Date().toLocaleDateString(),
            memo: v.memo
          };
        });

        // 검색어는 API 명세가 불명확하여 클라이언트에서 최소 필터링 유지
        let filtered = mappedVendors;
        if (searchTerm) {
          const lower = searchTerm.toLowerCase();
          filtered = filtered.filter(v => 
            v.name.toLowerCase().includes(lower) || 
            v.manager.toLowerCase().includes(lower) ||
            v.contact.includes(lower)
          );
        }

        setVendors(filtered);
        setStats(json.data.stats || {
          totalCount: filtered.length,
          activeCount: filtered.filter((v) => v.status === '활성').length,
          avgCommission: 0,
          basicCategoryCount: 0
        });
      }
    } catch (error) {
      console.error("Fetch vendors error:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createVendor = async (data: VendorCreateRequest): Promise<boolean> => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch('https://api.wedmin.biz/vendor', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      const json = await response.json();
      if (!response.ok || !json.status) {
        throw new Error(json.message || '등록 실패');
      }
      return true;
    } catch (error) {
      console.error("Create vendor error:", error);
      alert(`오류 발생: ${error instanceof Error ? error.message : '알 수 없는 오류'}`);
      return false;
    }
  };

  const linkExistingVendor = async (targetVendorIdx: number, localVendorIdx?: number): Promise<boolean> => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch('https://api.wedmin.biz/vendor/link-existing', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          targetVendorIdx,
          ...(typeof localVendorIdx === 'number' ? { localVendorIdx } : {})
        })
      });

      const json = await response.json().catch(() => null);
      if (!response.ok || (json && json.status === false)) {
        throw new Error(json?.message || '기존 업체 연결 실패');
      }
      return true;
    } catch (error) {
      console.error('Link existing vendor error:', error);
      alert(`오류 발생: ${error instanceof Error ? error.message : '알 수 없는 오류'}`);
      return false;
    }
  };

  const updateVendorStatus = async (vendorIdx: string, status: 'ACTIVE' | 'INACTIVE'): Promise<boolean> => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/vendor/${vendorIdx}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status })
      });

      const json = await response.json().catch(() => null);
      if (!response.ok || (json && json.status === false)) {
        throw new Error(json?.message || '상태 변경 실패');
      }
      return true;
    } catch (error) {
      console.error('Update vendor status error:', error);
      alert(`오류 발생: ${error instanceof Error ? error.message : '알 수 없는 오류'}`);
      return false;
    }
  };

  const deleteVendor = async (vendorIdx: string): Promise<boolean> => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/vendor/${vendorIdx}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const json = await response.json();
      if (!response.ok || !json.status) {
        throw new Error(json.message || '삭제 실패');
      }
      return true;
    } catch (error) {
      console.error("Delete vendor error:", error);
      alert("오류가 발생했습니다.");
      return false;
    }
  };

  return { vendors, stats, isLoading, fetchVendors, createVendor, linkExistingVendor, updateVendorStatus, deleteVendor };
};
